import { useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useRetailer } from '@lib/core/retailers/hooks/useRetailer';
import { BrowserUrlConsts } from '@lib/core/service/consts';
import { useApp } from '@lib/core/service/hooks';
import { prependBasename } from '@lib/core/service/utils';
import { useUser } from '@lib/core/users/hooks';
import { RouteUtils } from '@lib/tools/routes';
import { PRODUCT_CATEGORIES } from '@lib/tools/shared/helpers/consts';
import { useAddons } from '@lib/tools/views/hooks';
import { PAGES } from '@lib/tools/views/urls';

import { LoadingSpinner } from '@components/web/src/templates/Loading/LoadingSpinner/LoadingSpinner';

export const HomeRouteAccess = () => {
  const { isDisableHomeAddon } = useAddons();
  const { isUserAuthenticated } = useUser();
  const { isAuthenticatedRetailerAccess } = useRetailer();

  const page =
    isUserAuthenticated && isAuthenticatedRetailerAccess
      ? prependBasename(PAGES.vinhood.catalog)
      : prependBasename(PAGES.vinhood.quiz.chooseExpert);

  return isDisableHomeAddon ? <Navigate replace to={page} /> : <Outlet />;
};

export const AuthRoutesAccess = () => {
  const { isDisableHomeAddon } = useAddons();
  const { isUserHasCharacterForCurrentPC } = useUser();
  const { isAnonymousRetailerAccess } = useRetailer();

  const redirectToCatalogPage = prependBasename(PAGES.vinhood.catalog);
  const redirectToHomePage = prependBasename(PAGES.vinhood.home);

  // For anonymous widget we have to skip auth routes
  if (isAnonymousRetailerAccess) {
    return (
      <Navigate
        replace
        to={isUserHasCharacterForCurrentPC || isDisableHomeAddon ? redirectToCatalogPage : redirectToHomePage}
      />
    );
  }

  return <Outlet />;
};

export const PrivacyAccess = () => {
  const { pathname } = useLocation();
  const { isEnableWelcomePageAddon, isGamifiedSurveyQuestionsAddon } = useAddons();
  const { isUserAuthenticated } = useUser();
  const hasLandedOnSurveyGameRef = useRef(false);

  const isPrivacyAccepted = localStorage.getItem('isShowPrivacyPage') === 'true';
  const redirectToWelcomePrivacyPage = prependBasename(PAGES.vinhood.welcomePrivacy);
  const redirectToSignInPage = prependBasename(PAGES.vinhood.signIn);
  const redirectToSurveyGamePage = prependBasename(PAGES.vinhood.surveyGame);

  const isSignInPage = pathname.includes(PAGES.vinhood.signIn);
  const isSurveyGamePage = pathname.includes(PAGES.vinhood.surveyGame);
  const isLeaderboardPage = pathname.includes(PAGES.vinhood.leaderboard);

  if (isEnableWelcomePageAddon && !isPrivacyAccepted) {
    return <Navigate replace to={redirectToWelcomePrivacyPage} />;
  }

  if (isGamifiedSurveyQuestionsAddon && !isUserAuthenticated && !isSignInPage) {
    return <Navigate replace to={redirectToSignInPage} />;
  }

  if (isGamifiedSurveyQuestionsAddon && isUserAuthenticated) {
    if (isSurveyGamePage) {
      hasLandedOnSurveyGameRef.current = true;
    } else if (!hasLandedOnSurveyGameRef.current && !isLeaderboardPage) {
      return <Navigate replace to={redirectToSurveyGamePage} />;
    }
  }

  return <Outlet />;
};

export const ResultCatalogRouteAccess = () => {
  const { search } = useLocation();

  const hasRedirectedToCharacterPageRef = useRef(false);
  const {
    isEnableTastePathAddon,
    isExternalCharacterPage,
    isTasteIdResultPageAddon,
    isDisableHomeAddon,
    isEventsGamingAddon,
  } = useAddons();
  const {
    isUserAuthenticated,
    isUserCharactersLoading,
    isUserHasCharacterForCurrentPC,
    userBestMatchCharacterForCurrentServiceProductCategory,
  } = useUser();
  const { shouldShowTastePathStartPage, productCategory } = useApp();

  const searchParams = new URLSearchParams(search);
  const characterFromUrl = searchParams.get(BrowserUrlConsts.characters);

  const redirectToTastePathStartPage = prependBasename(PAGES.vinhood.tastePathStartPart);
  const redirectToQuizChooseExpert = prependBasename(PAGES.vinhood.quiz.chooseExpert);
  const redirectToHomePage = prependBasename(PAGES.vinhood.home);
  const redirectToTasteIdPage = prependBasename(PAGES.vinhood.tasteId.tasteId);
  const redirectToActionPage = prependBasename(PAGES.vinhood.action);
  const redirectToSuggestionPage = prependBasename(PAGES.vinhood.suggestion);

  if (isUserCharactersLoading) {
    return <LoadingSpinner />;
  }

  if (productCategory === PRODUCT_CATEGORIES.PRODUCT_CATEGORY_NICOTINE_POUCHES) {
    return <Navigate replace to={redirectToSuggestionPage} />;
  }

  if (isEventsGamingAddon && isUserAuthenticated && isUserHasCharacterForCurrentPC) {
    return <Navigate replace to={redirectToActionPage} />;
  }

  if (isTasteIdResultPageAddon) {
    return <Navigate replace to={redirectToTasteIdPage} />;
  }

  if (isExternalCharacterPage) {
    const redirectUrl = userBestMatchCharacterForCurrentServiceProductCategory?.characterRedirectUrl;

    if (redirectUrl?.length > 3 && !hasRedirectedToCharacterPageRef.current) {
      hasRedirectedToCharacterPageRef.current = true;
      RouteUtils.redirectExternalPage(redirectUrl);
    }

    if (redirectUrl) {
      return <Navigate replace to={isDisableHomeAddon ? redirectToTasteIdPage : redirectToHomePage} />;
    }

    return <Navigate replace to={redirectToQuizChooseExpert} />;
  }

  if (isEnableTastePathAddon && !isUserHasCharacterForCurrentPC && !isUserCharactersLoading && !characterFromUrl) {
    return <Navigate replace to={redirectToQuizChooseExpert} />;
  }

  if (isEnableTastePathAddon && isUserHasCharacterForCurrentPC && shouldShowTastePathStartPage && !characterFromUrl) {
    return <Navigate replace to={redirectToTastePathStartPage} />;
  }

  return <Outlet />;
};

/**
 * ! Handles old quiz URLs distributed prior to v0.7.0
 */
export const LegacyQuizRouteAccess = () => {
  const legacyRouteTasteQuiz = '/landing_test/';
  const legacyRouteRecipeQuiz = '/recipe_test/';
  const legacyRouteExpertQuiz = '/expert_test/';

  if (RouteUtils.currentPageContains(legacyRouteTasteQuiz)) {
    return <Navigate replace to={prependBasename(PAGES.vinhood.quiz.chooseTaste)} />;
  }
  if (RouteUtils.currentPageContains(legacyRouteRecipeQuiz)) {
    return <Navigate replace to={prependBasename(PAGES.vinhood.quiz.recipe)} />;
  }
  if (RouteUtils.currentPageContains(legacyRouteExpertQuiz)) {
    return <Navigate replace to={prependBasename(PAGES.vinhood.quiz.expert)} />;
  }

  return <Outlet />;
};

// Custom fix to redirect user to homepage after logging out
export const RedirectAnonymousAccess = () => {
  const { isUserAuthenticated } = useUser();
  const redirectToHomePage = prependBasename(PAGES.vinhood.home);
  if (!isUserAuthenticated) {
    return <Navigate replace to={redirectToHomePage} />;
  }
  return <Outlet />;
};
